import React, { useEffect, useState } from "react";
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, IconButton, Tooltip, TableSortLabel } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { getUsers } from "../services/api";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";

const API = "https://api.miksanplastik.com.tr";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [orderBy, setOrderBy] = useState("status");
  const [order, setOrder] = useState("asc");

  const load = () => getUsers().then(r => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(col);
      setOrder("asc");
    }
  };

  const sorted = [...users].sort((a, b) => {
    const statusOrder = { pending_approval: 0, pending: 1, approved: 2, rejected: 3 };
    let aVal = orderBy === "status" ? statusOrder[a.status] ?? 9 : (a[orderBy] || "");
    let bVal = orderBy === "status" ? statusOrder[b.status] ?? 9 : (b[orderBy] || "");
    if (typeof aVal === "string") aVal = aVal.toLowerCase();
    if (typeof bVal === "string") bVal = bVal.toLowerCase();
    if (aVal < bVal) return order === "asc" ? -1 : 1;
    if (aVal > bVal) return order === "asc" ? 1 : -1;
    return 0;
  });

  const approve = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/api/users/${id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Kullanici onaylandi ve bilgilendirildi!");
      load();
    } catch { toast.error("Hata olustu"); }
  };

  const reject = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/api/users/${id}/reject`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Kullanici reddedildi");
      load();
    } catch { toast.error("Hata olustu"); }
  };

  const deleteUser = async (id, name) => {
    if (!window.confirm(`"${name}" adli kullaniciyi silmek istediginize emin misiniz?`)) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/api/users/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Kullanici silindi");
      load();
    } catch { toast.error("Hata olustu"); }
  };

  const statusColor = (s) => {
    if (s === "approved") return "success";
    if (s === "pending_approval") return "warning";
    if (s === "pending") return "info";
    return "error";
  };

  const statusLabel = (s) => {
    if (s === "approved") return "Onayli";
    if (s === "pending_approval") return "Admin Onayi Bekliyor";
    if (s === "pending") return "Mail Dogrulama Bekliyor";
    return "Reddedildi";
  };

  const pendingCount = users.filter(u => u.status === "pending_approval").length;

  const cols = [
    { id: "name", label: "Ad Soyad" },
    { id: "email", label: "E-posta" },
    { id: "phone", label: "Telefon" },
    { id: "company", label: "Firma" },
    { id: "status", label: "Durum" },
    // Faz E.2: KDV gorunum kolonu — Customer360 toggle'inin liste yansimasi
    { id: "vat_effective", label: "KDV" },
  ];

  // Faz E.2: KDV rozet helper — backend response alanlarina gore renklendir
  // vat_user_override === null  -> "Grup" (default'tan miras, gri)
  // vat_user_override === 1     -> "Dahil" (yesil, override aktif)
  // vat_user_override === 0     -> "Hariç" (turuncu, override aktif)
  // Effective deger ayrica vat_effective alaninda dolu (0 veya 1).
  const renderVatChip = (u) => {
    if (u.vat_user_override === null || u.vat_user_override === undefined) {
      return <Chip label={u.vat_effective ? 'Grup → Dahil' : 'Grup → Hariç'} size="small"
                   sx={{ bgcolor: '#eee', color: '#555' }} />;
    }
    return u.vat_user_override
      ? <Chip label="Dahil" size="small" color="success" />
      : <Chip label="Hariç" size="small" color="warning" />;
  };

  return (
    <Box>
      <Toaster />
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Typography variant="h5" fontWeight={700}>Musteriler</Typography>
        {pendingCount > 0 && (
          <Chip label={`${pendingCount} onay bekliyor`} color="warning" size="small" />
        )}
        <Typography variant="body2" color="text.secondary">Toplam: {users.length}</Typography>
      </Box>
      <TableContainer component={Paper} sx={{ maxHeight: "calc(100vh - 220px)" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {cols.map(col => (
                <TableCell key={col.id} sx={{ bgcolor: "#f5f5f5", fontWeight: 700 }}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : "asc"}
                    onClick={() => handleSort(col.id)}>
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: 700 }}>İşlem</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map(u => (
              <TableRow key={u.id} hover
                sx={{ bgcolor: u.status === "pending_approval" ? "#FFF8E1" : "inherit" }}>
                <TableCell>{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.phone || "-"}</TableCell>
                <TableCell>{u.company || "-"}</TableCell>
                <TableCell>
                  <Chip label={statusLabel(u.status)} color={statusColor(u.status)} size="small" />
                </TableCell>
                {/* Faz E.2: KDV gorunum rozetı (Grup default veya admin override) */}
                <TableCell>{renderVatChip(u)}</TableCell>
                <TableCell>
                  {(u.status === "pending_approval" || u.status === "rejected") && (
                    <Tooltip title="Onayla">
                      <IconButton color="success" onClick={() => approve(u.id)}>
                        <CheckCircleIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {u.status !== "rejected" && (
                    <Tooltip title="Reddet">
                      <IconButton color="warning" onClick={() => reject(u.id)}>
                        <CancelIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {/* Faz F: Müşteri 360 — tek tıkla tam profile git */}
                  <Tooltip title="Müşteri 360 (tam profil)">
                    <IconButton color="primary" onClick={() => window.open(`/customer/${u.id}`, '_blank')}>
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Sil">
                    <IconButton color="error" onClick={() => deleteUser(u.id, u.name)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}